import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Settings, Lock, Hash, RefreshCw, LogOut, CheckCircle, AlertCircle, Users } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPanel = ({ isOpen, onClose, language }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [counterInfo, setCounterInfo] = useState(null);
  const [newStartingNumber, setNewStartingNumber] = useState('');

  const t = {
    de: {
      title: 'Admin-Bereich',
      loginTitle: 'Admin-Anmeldung',
      loginDescription: 'Geben Sie den Admin-Schlüssel ein, um fortzufahren',
      adminKey: 'Admin-Schlüssel',
      adminKeyPlaceholder: 'Admin-Schlüssel eingeben',
      login: 'Anmelden',
      logout: 'Abmelden',
      customerNumberManagement: 'Kundennummer-Verwaltung',
      currentStatus: 'Aktueller Status',
      nextNumber: 'Nächste Kundennummer',
      totalGenerated: 'Bisher generiert',
      resetCounter: 'Zähler zurücksetzen',
      newStartingNumber: 'Neue Startnummer',
      newStartingNumberPlaceholder: 'z.B. 21223',
      resetButton: 'Zurücksetzen',
      resetSuccess: 'Zähler erfolgreich zurückgesetzt',
      resetError: 'Fehler beim Zurücksetzen',
      loginError: 'Ungültiger Admin-Schlüssel',
      loading: 'Laden...',
      refresh: 'Aktualisieren'
    },
    en: {
      title: 'Admin Panel',
      loginTitle: 'Admin Login',
      loginDescription: 'Enter the admin key to continue',
      adminKey: 'Admin Key',
      adminKeyPlaceholder: 'Enter admin key',
      login: 'Login',
      logout: 'Logout',
      customerNumberManagement: 'Customer Number Management',
      currentStatus: 'Current Status',
      nextNumber: 'Next Customer Number',
      totalGenerated: 'Total Generated',
      resetCounter: 'Reset Counter',
      newStartingNumber: 'New Starting Number',
      newStartingNumberPlaceholder: 'e.g. 21223',
      resetButton: 'Reset',
      resetSuccess: 'Counter reset successfully',
      resetError: 'Error resetting counter',
      loginError: 'Invalid admin key',
      loading: 'Loading...',
      refresh: 'Refresh'
    }
  };

  const text = t[language] || t.en;

  const fetchCounterInfo = async () => {
    if (!adminKey) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/current-customer-number?admin_key=${encodeURIComponent(adminKey)}`);
      
      if (response.ok) {
        const data = await response.json();
        setCounterInfo(data);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        toast.error(text.loginError);
      }
    } catch (error) {
      console.error('Error fetching counter info:', error);
      toast.error(text.loginError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    await fetchCounterInfo();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminKey('');
    setCounterInfo(null);
    setNewStartingNumber('');
  };

  const handleReset = async () => {
    if (!newStartingNumber || isNaN(parseInt(newStartingNumber))) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/reset-customer-number`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          admin_key: adminKey,
          new_starting_number: parseInt(newStartingNumber)
        }),
      });

      if (response.ok) {
        toast.success(text.resetSuccess);
        setNewStartingNumber('');
        await fetchCounterInfo(); // Refresh the counter info
      } else {
        toast.error(text.resetError);
      }
    } catch (error) {
      console.error('Error resetting counter:', error);
      toast.error(text.resetError);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setIsAuthenticated(false);
      setAdminKey('');
      setCounterInfo(null);
      setNewStartingNumber('');
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            {text.title}
          </DialogTitle>
          {!isAuthenticated && (
            <DialogDescription>{text.loginDescription}</DialogDescription>
          )}
        </DialogHeader>

        {!isAuthenticated ? (
          // Login Form
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="adminKey" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                {text.adminKey}
              </Label>
              <Input
                id="adminKey"
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder={text.adminKeyPlaceholder}
                className="input-focus-ring"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !adminKey}
            >
              {isLoading ? text.loading : text.login}
            </Button>
          </form>
        ) : (
          // Admin Dashboard
          <div className="space-y-4">
            {/* Current Status */}
            <Card className="border bg-muted/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Hash className="w-4 h-4 text-primary" />
                  {text.currentStatus}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {counterInfo && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">{text.nextNumber}</span>
                      <span className="font-mono font-bold text-lg text-primary">
                        {counterInfo.next_customer_number}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">{text.totalGenerated}</span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="font-semibold">{counterInfo.total_customers_generated}</span>
                      </span>
                    </div>
                  </>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchCounterInfo}
                  disabled={isLoading}
                  className="w-full"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                  {text.refresh}
                </Button>
              </CardContent>
            </Card>

            <Separator />

            {/* Reset Counter */}
            <Card className="border border-destructive/20 bg-destructive/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-destructive" />
                  {text.resetCounter}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="newStartingNumber" className="text-sm">
                    {text.newStartingNumber}
                  </Label>
                  <div className="flex gap-2">
                    <div className="flex items-center px-3 bg-muted rounded-l-md border border-r-0">
                      <span className="text-sm font-mono text-muted-foreground">RB-</span>
                    </div>
                    <Input
                      id="newStartingNumber"
                      type="number"
                      value={newStartingNumber}
                      onChange={(e) => setNewStartingNumber(e.target.value)}
                      placeholder={text.newStartingNumberPlaceholder}
                      className="input-focus-ring rounded-l-none"
                      min="1"
                    />
                  </div>
                </div>
                <Button 
                  variant="destructive"
                  onClick={handleReset}
                  disabled={isLoading || !newStartingNumber}
                  className="w-full"
                >
                  {isLoading ? text.loading : text.resetButton}
                </Button>
              </CardContent>
            </Card>

            <Separator />

            {/* Logout Button */}
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="w-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {text.logout}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminPanel;
